import { all, call, fork, takeEvery } from '@redux-saga/core/effects';
import { createReducer } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import {
  createGlobalContentInitialize,
  createGlobalContentSuccess,
  getLatestGlobalContentOfTypeError,
  getLatestGlobalContentOfTypeInitialize,
  getLatestGlobalContentOfTypeSuccess,
  removeGlobalContentWithIdFromStore,
} from '../actions/api';
import { GlobalContentConnector } from '../connectors/globalContentConnector';
import { getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
} from '../helpers/sagaHelpers';
import {
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';

// -------- Overview --------

// The following endpoints exist
//  - GET /globalContent/{typeId}/latest
//  - POST /globalContent/{typeId}

// -------- Reducer --------
const globalContentReducer = createReducer(
  {
    all: [],
    isFetchingGlobalContent: false,
  },
  (builder) => {
    builder
      .addCase(getLatestGlobalContentOfTypeInitialize, (state) => {
        state.isFetchingGlobalContent = true;
      })
      .addCase(getLatestGlobalContentOfTypeError, (state) => {
        state.isFetchingGlobalContent = false;
      })
      .addCase(getLatestGlobalContentOfTypeSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingGlobalContent: false,
        });
        const globalContent = getRequestData(action);
        return updateDtoList(newState, 'all', globalContent);
      })
      .addCase(createGlobalContentSuccess, (state, action) => {
        const globalContent = getRequestData(action);
        return updateDtoList(state, 'all', globalContent);
      })
      .addCase(removeGlobalContentWithIdFromStore, (state, action) => {
        const idToRemove = action.payload;
        return removeItemFromList(state, 'all', idToRemove);
      });
  },
);

// -------- Sagas ---------
export function* globalContentSaga() {
  yield all([fork(getGlobalContentSaga), fork(createGlobalContentSaga)]);
}

function* getGlobalContentSaga() {
  yield takeEvery(
    getLatestGlobalContentOfTypeInitialize.type,
    generateGetAllApiCall(GlobalContentConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.getLatestGlobalContentOfType],
        action.payload,
      );
    }),
  );
}

function* createGlobalContentSaga() {
  yield takeEvery(
    createGlobalContentInitialize.type,
    generateCreateApiCall(GlobalContentConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.createGlobalContent],
        action.payload.globalContentTypeId,
        action.payload,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetGlobalContent = (state) =>
  selectAllFromApiState(state, 'globalContent');

const allGlobalContentSelector = createSelector(
  selectGetGlobalContent,
  (globalContent) => globalContent,
);

const getLatestGlobalContentByGlobalContentTypeId = (globalContentTypeId) =>
  createSelector(allGlobalContentSelector, (globalContents) =>
    globalContents.find(
      (globalContent) =>
        getRelId(globalContent, 'globalContentType') === globalContentTypeId,
    ),
  );

const globalContentState = (state) => state.api.globalContent;

const isFetchingGlobalContentSelector = createSelector(
  globalContentState,
  (state) => state.isFetchingGlobalContent,
);

export {
  getLatestGlobalContentByGlobalContentTypeId,
  allGlobalContentSelector,
  isFetchingGlobalContentSelector,
};

export default globalContentReducer;
